import type { FileCategoryGroup } from '@modules/product/type/file-category';
import type LibraryDownload from '@modules/product/type/library-download';

/** File extensions that indicate Pro Design files (CAD/BIM). */
const PRO_DESIGN_EXTENSIONS = new Set([
    'rvt',
    'rfa',
    'dwg',
    'dxf',
    'ifc',
    'skp',
    'step',
    'stp',
    'iges',
    'igs',
    '3dm',
    'pln',
]);

/** Title keywords (lowercased) that indicate Safety & Compliance files. */
const SAFETY_KEYWORDS = [
    'sds',
    'msds',
    'safety',
    'warranty',
    'branz',
    'codemark',
    'fire test',
    'compliance',
    'appraisal',
    'certificate',
];

/**
 * Extracts the file extension from a URL or filename string.
 * Handles query parameters and hash fragments.
 */
export const getFileExtension = (link: string): string => {
    try {
        // Remove query params and hash
        const cleanPath = (link.split('?')[0] ?? '').split('#')[0] ?? '';
        // Only the last path segment. A URL ending in a page rather than a
        // file has no extension, and the last dot in it belongs to the
        // hostname — `archipro.co.nz/collections/wood-fires` was reporting an
        // extension of `nz/collections/wood-fires`.
        const lastSegment = cleanPath.split('/').pop() ?? '';
        const lastDot = lastSegment.lastIndexOf('.');

        if (lastDot === -1) return '';

        const extension = lastSegment.substring(lastDot + 1).toLowerCase();
        // Extensions are short and alphanumeric. Anything else is a path that
        // happened to contain a dot.
        return /^[a-z0-9]{1,6}$/.test(extension) ? extension : '';
    } catch {
        return '';
    }
};

/**
 * Classifies a LibraryDownload into one of three tiers:
 * - Safety: compliance docs (SDS, warranties, BRANZ, etc.)
 * - Specs: product specifications, technical data, guides (default)
 * - ProDesign: BIM/CAD models, project packs, price lists
 *
 * Classification priority:
 * 1. Use FileCategory.Group if present
 * 2. Infer from file extension (CAD/BIM extensions -> ProDesign)
 * 3. Infer from title keywords (safety-related -> Safety)
 * 4. Default to Specs
 */
export const classifyDownload = (
    download: LibraryDownload,
): FileCategoryGroup => {
    // 1. Use backend-provided group if available
    if (download.FileCategory?.Group) {
        return download.FileCategory.Group;
    }

    // 2. Infer from file extension
    const link = download.ExternalLink || download.InternalLink || '';
    const ext = getFileExtension(link);

    if (ext && PRO_DESIGN_EXTENSIONS.has(ext)) {
        return 'ProDesign';
    }

    // 3. Infer from title keywords
    const titleLower = download.Title.toLowerCase();
    if (SAFETY_KEYWORDS.some((keyword) => titleLower.includes(keyword))) {
        return 'Safety';
    }

    // 4. Default to Specs
    return 'Specs';
};

interface CategoryGroup {
    categoryTitle: string;
    categoryId: number;
    downloads: LibraryDownload[];
}

const DEFAULT_CATEGORY_ID = 0;
const DEFAULT_CATEGORY_TITLE = 'Other';

/** Expands "BPIR" abbreviation to its full name. */
const normaliseCategoryTitle = (title: string): string =>
    title === 'BPIR' ? 'Building Product Information Requirements' : title;

/**
 * Groups downloads by their FileCategory title, sorted by category SortOrder.
 * Downloads without a category are placed under "Other".
 */
export const groupDownloadsByCategory = (
    downloads: LibraryDownload[],
): CategoryGroup[] => {
    const map = new Map<number, CategoryGroup>();

    for (const download of downloads) {
        const cat = download.FileCategory;
        const id = cat?.ID ?? DEFAULT_CATEGORY_ID;
        const title = normaliseCategoryTitle(
            cat?.Title ?? DEFAULT_CATEGORY_TITLE,
        );
        const sortOrder = cat?.SortOrder ?? Number.MAX_SAFE_INTEGER;

        if (!map.has(id)) {
            map.set(id, {
                categoryTitle: title,
                categoryId: id,
                downloads: [],
            });
        }

        map.get(id)!.downloads.push(download);

        // Store sortOrder for later sorting (attach to first occurrence)
        (map.get(id) as CategoryGroup & { _sort?: number })._sort = sortOrder;
    }

    return Array.from(map.values()).sort((a, b) => {
        const sortA =
            (a as CategoryGroup & { _sort?: number })._sort ??
            Number.MAX_SAFE_INTEGER;
        const sortB =
            (b as CategoryGroup & { _sort?: number })._sort ??
            Number.MAX_SAFE_INTEGER;
        return sortA - sortB;
    });
};
